import React, { useEffect, useState } from "react";
import { ContainerDiv, LoadingIcon } from "../../../../components";
import TomatoGrowerDashboard from "../tomatoGrowerDashboard/TomatoGrowerDashboard";
import BroccoliDashboard from "../broccoliGrowerDashboard/BroccoliDashboard";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useLocalStorage } from "../../../../hooks";
import { useUser } from "../../../../context/UserContext";
import { getCrops, getOrgs, getSites } from "../../../../utils/api";
import SpinachDashboard from "../spinachGrowerDashboard/SpinachDashboard";
import ScanningSessions from "../../scanningSessions/ScanningSessions";
import PERMISSIONS from "../../../../consts/permissions";
import { getDefaultPath } from "../../../../utils/defaultRedirectUtils";
import Reporting from "../../Reporting/Reporting";
import SpinachAnalysisDashboard from "../../SpinachAnalysis/SpinachAnalysisDashboard";
import BroccoliAnalysis from "../../broccoliAnalysis/BroccoliAnalysis";
import { SpinachHarvestOptimiserDashboard } from "../../spinachHarvestOptimiserDashboard/index.js";
import { BroccoliHarvestOptimiserDashboard } from "../../broccoliHarvestOptimiserDashboard/index.js";
import { HarvestEntryPage } from "../../spinachHarvestEntry/index.js";
import { BroccoliHarvestEntryPage } from "../../broccoliHarvestEntry/index.js";

const GrowerDashboard = ({ componentType = "default" }) => {
  const [orgData, setOrgData] = useState([]);
  const [currentOrg, setCurrentOrg] = useState(null);
  const [orgLoading, setOrgLoading] = useState(false);

  const [siteData, setSiteData] = useState([]);
  const [currentSite, setCurrentSite] = useState(null);
  const [siteLoading, setSiteLoading] = useState(false);

  const [cropData, setCropData] = useState([]);
  const [currentCrop, setCurrentCrop] = useState(null);
  const [cropLoading, setCropLoading] = useState(false);

  const [settingDefaults, setSettingDefaults] = useState({});
  const [dropdownLoading, setDropdownLoading] = useState(true);

  const [defaultDropdown, setDefaultDropdown] = useLocalStorage("dropdowns", null);

  const [searchParams, setSearchParams] = useSearchParams();

  const { userData, isAdmin, hasOrgPermission } = useUser();
  const navigate = useNavigate();

  // ---------------------------
  //  API calls
  // ---------------------------
  const fetchOrgs = async () => {
    setOrgLoading(true);
    const res = await getOrgs();
    const newData = res.data.data
      .filter((org) => org.is_active && org.is_grower)
      .map((org) => ({
        name: org.name,
        id: org.id,
      }))
      .sort((a, b) =>
        (a?.name || "").localeCompare(b?.name || "", undefined, {
          sensitivity: "base",
        }),
      );
    setOrgData(newData);
    setOrgLoading(false);
  };

  const fetchSites = async (orgId) => {
    setSiteLoading(true);
    const res = await getSites(orgId);
    const data = res.data.data
      .filter((site) => site.is_active)
      .map((site) => ({
        name: site.name,
        displayName: site.display_name,
        id: site.id,
        metadata: site.metadata,
        lat: site.lat,
        long: site.long,
      }));

    setSiteData(data);
    setSiteLoading(false);
  };

  const fetchCrops = async (site) => {
    setCropLoading(true);
    const res = await getCrops(site.id);
    const data = res?.data?.data
      .filter((crop) => crop.is_active)
      .map((crop) => ({
        name: crop.name,
        displayName: crop.display_name,
        id: crop.id,
      }));
    setCropData(data);
    setCropLoading(false);
  };

  // ---------------------------
  //  On mount: fetch orgs or sites
  // ---------------------------
  useEffect(() => {
    const fetchData = async () => {
      if (userData) {
        try {
          if (userData.is_superuser) {
            await fetchOrgs();
          } else {
            setCurrentOrg(userData.organization_id);

            const res = await getSites(userData.organization_id);
            const filteredData = res?.data?.data.filter((site) => site.is_active);
            const mappedData = filteredData.map((site) => ({
              name: site.name,
              displayName: site.display_name,
              id: site.id,
              metadata: site.metadata,
              lat: site.lat,
              long: site.long,
            }));
            setSiteData(mappedData);
          }
        } catch (error) {
          console.error("Error fetching data:", error);
        }
      }
    };
    fetchData();
  }, [userData]);

  // ---------------------------
  //  Org selection logic
  // ---------------------------
  useEffect(() => {
    if (orgData && orgData.length) {
      let params = new URLSearchParams(searchParams);
      let orgIdFromUrlParams = params.get("org");

      if (orgIdFromUrlParams) {
        // console.log("SETTING ORG FROM URL PARAMS");
        const orgFromUrl = orgData.find((item) => item.id === orgIdFromUrlParams);
        if (orgFromUrl) {
          setCurrentOrg(orgFromUrl.id);
        } else {
          // console.log("SETTING FIRST Org OF DATA");
          setCurrentOrg(defaultDropdown?.org || orgData[0]?.id);
        }
        setSettingDefaults({ ...settingDefaults, org: true });
      } else if (!settingDefaults.org) {
        // console.log("SETTING DEFAULT ORG");
        setCurrentOrg(defaultDropdown?.org || orgData[0]?.id);
        setSettingDefaults({ ...settingDefaults, org: true });
      } else {
        // console.log("SETTING FIRST ORG OF DATA");
        setCurrentOrg(orgData[0]?.id);
      }
    }
  }, [orgData]);

  // Whenever currentOrg changes, update localStorage and query param
  useEffect(() => {
    if (currentOrg) {
      if (searchParams.get("org") !== currentOrg) {
        let existingParams = new URLSearchParams(searchParams);
        existingParams.set("org", currentOrg);
        setSearchParams(existingParams);
      }

      setDefaultDropdown({
        ...defaultDropdown,
        org: currentOrg,
      });
    }
  }, [currentOrg]);

  // If superuser, fetch sites when org changes
  useEffect(() => {
    if (currentOrg && userData?.is_superuser) {
      fetchSites(currentOrg);
    }
  }, [currentOrg]);

  // ---------------------------
  //  Site selection logic (ID‐based)
  // ---------------------------
  useEffect(() => {
    if (siteData && siteData.length) {
      let params = new URLSearchParams(searchParams);
      let siteIdFromUrlParams = params.get("site");

      if (siteIdFromUrlParams) {
        // console.log("SETTING SITE FROM URL PARAMS");
        const siteFromUrl = siteData.find((item) => item.id === siteIdFromUrlParams);
        if (siteFromUrl) {
          setCurrentSite(siteFromUrl);
        } else {
          // console.log("SETTING FIRST SITE OF DATA");
          setCurrentSite(siteData[0]);
        }
      } else if (!settingDefaults.site) {
        // console.log("SETTING DEFAULT SITE from localStorage");
        const storedSiteId = defaultDropdown?.[currentOrg]?.site;
        if (storedSiteId) {
          const siteVal = siteData.find((s) => s.id === storedSiteId);
          setCurrentSite(siteVal ? siteVal : siteData[0]);
        } else {
          // fallback to first
          setCurrentSite(siteData[0]);
        }
        setSettingDefaults({ ...settingDefaults, site: true });
      } else {
        // console.log("SETTING FIRST SITE OF DATA");
        setCurrentSite(siteData[0]);
      }
    } else {
      setCurrentSite(null);
    }
  }, [siteData]);

  // Whenever currentSite changes, update localStorage + query param
  useEffect(() => {
    if (currentSite) {
      if (searchParams.get("site") !== currentSite.id) {
        let existingParams = new URLSearchParams(searchParams);
        existingParams.set("site", currentSite.id);
        setSearchParams(existingParams);
      }

      let orgObj = defaultDropdown ? defaultDropdown[currentOrg] : {};
      setDefaultDropdown({
        ...defaultDropdown,
        [currentOrg]: {
          ...orgObj,
          site: currentSite.id, // <--- store ONLY the site ID
        },
      });
    }
  }, [currentSite]);

  // Fetch crops on site change
  useEffect(() => {
    if (currentSite) {
      fetchCrops(currentSite);
    }
  }, [currentSite]);

  // ---------------------------
  //  Crop selection logic (ID‐based)
  // ---------------------------
  useEffect(() => {
    if (cropData && cropData.length) {
      let params = new URLSearchParams(searchParams);
      let cropIdFromUrlParams = params.get("crop");

      if (cropIdFromUrlParams) {
        // console.log("SETTING CROP FROM URL PARAMS");
        const cropFromUrl = cropData.find((c) => c.id === cropIdFromUrlParams);
        if (cropFromUrl) {
          setCurrentCrop(cropFromUrl);
        } else {
          // console.log("SETTING FIRST Crop OF DATA");
          setCurrentCrop(cropData[0]);
        }
      } else if (!settingDefaults.crop) {
        // console.log("SETTING DEFAULT CROP from localStorage");
        const storedCropId = defaultDropdown?.[currentOrg]?.crop;
        if (storedCropId) {
          const matchingCrop = cropData.find((c) => c.id === storedCropId);
          setCurrentCrop(matchingCrop ? matchingCrop : cropData[0]);
        } else {
          setCurrentCrop(cropData[0]);
        }
        setSettingDefaults({ ...settingDefaults, crop: true });
      } else {
        // console.log("SETTING FIRST CROP OF DATA");
        setCurrentCrop(cropData[0]);
      }
    } else {
      setCurrentCrop(null);
    }
  }, [cropData]);

  // Whenever currentCrop changes, update localStorage + query param
  useEffect(() => {
    if (currentCrop) {
      if (searchParams.get("crop") !== currentCrop.id) {
        let existingParams = new URLSearchParams(searchParams);
        existingParams.set("crop", currentCrop.id);
        setSearchParams(existingParams);
      }

      let orgObj = defaultDropdown ? defaultDropdown[currentOrg] : {};
      setDefaultDropdown({
        ...defaultDropdown,
        [currentOrg]: {
          ...orgObj,
          crop: currentCrop.id,
        },
      });
    }
  }, [currentCrop]);

  // ---------------------------
  //  Dropdown loading
  // ---------------------------
  useEffect(() => {
    setDropdownLoading(orgLoading || siteLoading || cropLoading);
  }, [orgLoading, cropLoading, siteLoading]);

  // ---------------------------
  //  Render dashboards
  // ---------------------------
  const renderDashboard = () => {
    if (!currentCrop || dropdownLoading) return null;

    if (currentCrop?.name === "tomato" && componentType === "reporting") {
      return (
        <Reporting
          currentOrg={currentOrg}
          setCurrentOrg={setCurrentOrg}
          orgData={orgData}
          setOrgData={setOrgData}
          orgLoading={orgLoading}
          setOrgLoading={setOrgLoading}
          currentSite={currentSite}
          setCurrentSite={setCurrentSite}
          siteData={siteData}
          setSiteData={setSiteData}
          siteLoading={siteLoading}
          setSiteLoading={setSiteLoading}
          currentCrop={currentCrop}
          setCurrentCrop={setCurrentCrop}
          cropData={cropData}
          setCropData={setCropData}
          cropLoading={cropLoading}
          setCropLoading={setCropLoading}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          settingDefaults={settingDefaults}
          setSettingDefaults={setSettingDefaults}
        />
      );
    }

    if (currentCrop?.name === "tomato" && componentType === "scanning_sessions") {
      return (
        <ScanningSessions
          currentOrg={currentOrg}
          setCurrentOrg={setCurrentOrg}
          orgData={orgData}
          setOrgData={setOrgData}
          orgLoading={orgLoading}
          setOrgLoading={setOrgLoading}
          currentSite={currentSite}
          setCurrentSite={setCurrentSite}
          siteData={siteData}
          setSiteData={setSiteData}
          siteLoading={siteLoading}
          setSiteLoading={setSiteLoading}
          currentCrop={currentCrop}
          setCurrentCrop={setCurrentCrop}
          cropData={cropData}
          setCropData={setCropData}
          cropLoading={cropLoading}
          setCropLoading={setCropLoading}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          settingDefaults={settingDefaults}
          setSettingDefaults={setSettingDefaults}
        />
      );
    }

    if (
      currentCrop?.name === "tomato" ||
      currentCrop?.name === "strawberry" ||
      currentCrop?.name === "blueberry"
    ) {
      // Check needed permissions:
      const requiredPermissions = [
        PERMISSIONS.HARVEST_SCHEDULE.VIEW,
        PERMISSIONS.HARVEST_SCHEDULE.EDIT,
      ];
      const userPermissions = new Set(userData.permissions.map((p) => p.name));
      const hasRequiredPermission = requiredPermissions.some((perm) => userPermissions.has(perm));
      if (!(hasRequiredPermission || userData.is_superuser)) {
        const defaultPath = getDefaultPath({
          userData,
          isAdmin,
          hasOrgPermission,
          skipPath: "/dashboard",
        });
        navigate(defaultPath, { replace: true });
        return null;
      }
      return (
        <TomatoGrowerDashboard
          currentOrg={currentOrg}
          setCurrentOrg={setCurrentOrg}
          orgData={orgData}
          setOrgData={setOrgData}
          orgLoading={orgLoading}
          setOrgLoading={setOrgLoading}
          currentSite={currentSite}
          setCurrentSite={setCurrentSite}
          siteData={siteData}
          setSiteData={setSiteData}
          siteLoading={siteLoading}
          setSiteLoading={setSiteLoading}
          currentCrop={currentCrop}
          setCurrentCrop={setCurrentCrop}
          cropData={cropData}
          setCropData={setCropData}
          cropLoading={cropLoading}
          setCropLoading={setCropLoading}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          settingDefaults={settingDefaults}
          setSettingDefaults={setSettingDefaults}
        />
      );
    }

    if (currentCrop?.name === "broccoli" && componentType === "broccoli_analysis") {
      return (
        <BroccoliAnalysis
          currentOrg={currentOrg}
          setCurrentOrg={setCurrentOrg}
          orgData={orgData}
          setOrgData={setOrgData}
          orgLoading={orgLoading}
          setOrgLoading={setOrgLoading}
          currentSite={currentSite}
          setCurrentSite={setCurrentSite}
          siteData={siteData}
          setSiteData={setSiteData}
          siteLoading={siteLoading}
          setSiteLoading={setSiteLoading}
          currentCrop={currentCrop}
          setCurrentCrop={setCurrentCrop}
          cropData={cropData}
          setCropData={setCropData}
          cropLoading={cropLoading}
          setCropLoading={setCropLoading}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          settingDefaults={settingDefaults}
          setSettingDefaults={setSettingDefaults}
        />
      );
    }

    if (currentCrop?.name === "broccoli" && componentType === "broccoli_harvest_optimizer") {
      return (
        <BroccoliHarvestOptimiserDashboard
          currentOrg={currentOrg}
          setCurrentOrg={setCurrentOrg}
          orgData={orgData}
          setOrgData={setOrgData}
          orgLoading={orgLoading}
          setOrgLoading={setOrgLoading}
          currentSite={currentSite}
          setCurrentSite={setCurrentSite}
          siteData={siteData}
          setSiteData={setSiteData}
          siteLoading={siteLoading}
          setSiteLoading={setSiteLoading}
          currentCrop={currentCrop}
          setCurrentCrop={setCurrentCrop}
          cropData={cropData}
          setCropData={setCropData}
          cropLoading={cropLoading}
          setCropLoading={setCropLoading}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          settingDefaults={settingDefaults}
          setSettingDefaults={setSettingDefaults}
        />
      );
    }

    if (currentCrop?.name === "broccoli" && componentType === "broccoli_harvest_entry") {
      return (
        <BroccoliHarvestEntryPage
          currentOrg={currentOrg}
          setCurrentOrg={setCurrentOrg}
          orgData={orgData}
          currentSite={currentSite}
          setCurrentSite={setCurrentSite}
          siteData={siteData}
        />
      );
    }

    if (currentCrop?.name === "broccoli") {
      return (
        <BroccoliDashboard
          currentOrg={currentOrg}
          setCurrentOrg={setCurrentOrg}
          orgData={orgData}
          setOrgData={setOrgData}
          orgLoading={orgLoading}
          setOrgLoading={setOrgLoading}
          currentSite={currentSite}
          setCurrentSite={setCurrentSite}
          siteData={siteData}
          setSiteData={setSiteData}
          siteLoading={siteLoading}
          setSiteLoading={setSiteLoading}
          currentCrop={currentCrop}
          setCurrentCrop={setCurrentCrop}
          cropData={cropData}
          setCropData={setCropData}
          cropLoading={cropLoading}
          setCropLoading={setCropLoading}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          settingDefaults={settingDefaults}
          setSettingDefaults={setSettingDefaults}
        />
      );
    }

    if (currentCrop?.name === "spinach" && componentType === "spinach_analysis") {
      return (
        <SpinachAnalysisDashboard
          currentOrg={currentOrg}
          setCurrentOrg={setCurrentOrg}
          orgData={orgData}
          setOrgData={setOrgData}
          orgLoading={orgLoading}
          setOrgLoading={setOrgLoading}
          currentSite={currentSite}
          setCurrentSite={setCurrentSite}
          siteData={siteData}
          setSiteData={setSiteData}
          siteLoading={siteLoading}
          setSiteLoading={setSiteLoading}
          currentCrop={currentCrop}
          setCurrentCrop={setCurrentCrop}
          cropData={cropData}
          setCropData={setCropData}
          cropLoading={cropLoading}
          setCropLoading={setCropLoading}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          settingDefaults={settingDefaults}
          setSettingDefaults={setSettingDefaults}
        />
      );
    }

    if (currentCrop?.name === "spinach" && componentType === "spinach_harvest_optimizer") {
      return (
        <SpinachHarvestOptimiserDashboard
          currentOrg={currentOrg}
          setCurrentOrg={setCurrentOrg}
          orgData={orgData}
          setOrgData={setOrgData}
          orgLoading={orgLoading}
          setOrgLoading={setOrgLoading}
          currentSite={currentSite}
          setCurrentSite={setCurrentSite}
          siteData={siteData}
          setSiteData={setSiteData}
          siteLoading={siteLoading}
          setSiteLoading={setSiteLoading}
          currentCrop={currentCrop}
          setCurrentCrop={setCurrentCrop}
          cropData={cropData}
          setCropData={setCropData}
          cropLoading={cropLoading}
          setCropLoading={setCropLoading}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          settingDefaults={settingDefaults}
          setSettingDefaults={setSettingDefaults}
        />
      );
    }

    if (currentCrop?.name === "spinach" && componentType === "spinach_harvest_entry") {
      return (
        <HarvestEntryPage
          currentOrg={currentOrg}
          setCurrentOrg={setCurrentOrg}
          orgData={orgData}
          setOrgData={setOrgData}
          orgLoading={orgLoading}
          setOrgLoading={setOrgLoading}
          currentSite={currentSite}
          setCurrentSite={setCurrentSite}
          siteData={siteData}
          setSiteData={setSiteData}
          siteLoading={siteLoading}
          setSiteLoading={setSiteLoading}
          currentCrop={currentCrop}
          setCurrentCrop={setCurrentCrop}
          cropData={cropData}
          setCropData={setCropData}
          cropLoading={cropLoading}
          setCropLoading={setCropLoading}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          settingDefaults={settingDefaults}
          setSettingDefaults={setSettingDefaults}
        />
      );
    }

    if (currentCrop?.name === "spinach") {
      return (
        <SpinachDashboard
          currentOrg={currentOrg}
          setCurrentOrg={setCurrentOrg}
          orgData={orgData}
          setOrgData={setOrgData}
          orgLoading={orgLoading}
          setOrgLoading={setOrgLoading}
          currentSite={currentSite}
          setCurrentSite={setCurrentSite}
          siteData={siteData}
          setSiteData={setSiteData}
          siteLoading={siteLoading}
          setSiteLoading={setSiteLoading}
          currentCrop={currentCrop}
          setCurrentCrop={setCurrentCrop}
          cropData={cropData}
          setCropData={setCropData}
          cropLoading={cropLoading}
          setCropLoading={setCropLoading}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          settingDefaults={settingDefaults}
          setSettingDefaults={setSettingDefaults}
        />
      );
    }

    return <div>Please select a valid crop.</div>;
  };

  return (
    <>
      {orgLoading || siteLoading || cropLoading ? (
        <LoadingIcon height="200px" classname="absolute h-full" />
      ) : (
        <ContainerDiv>{renderDashboard()}</ContainerDiv>
      )}
    </>
  );
};

export default GrowerDashboard;
